// Extra coverages + chosen options per product for the confirmation email, derived from the SAME
// descriptors the product screens render (Supportsection.Dekkingen flags, i18n label keys), so the
// email cannot drift from the screen. Order = screen order.

import type { Lang, ProductId } from './saaEmailCopy';
import { AUTO_OPTIONAL_COVERAGES } from '../pages/screens/personenauto/coverages';
import { INBOEDEL_OPTIONAL_COVERAGES, getEc } from '../pages/screens/inboedel/coverages';
import { RBV_COVERAGES } from '../pages/screens/rechtsbijstand/coverages';

export type EmailTranslate = (key: string, opts?: Record<string, unknown>) => string;

export interface CoverageDef {
  flag: string; // Supportsection.Dekkingen.<flag>, set to 'J' when selected
  labelKey: string; // i18n key — the same label the screen renders
  /** Insured sum shown after the label ("Lijfsieraden: € 25.000") when the coverage carries one. */
  sum?: (productData: any) => unknown;
}

/** One chosen option line (e.g. the eigen risico picked on screen); null when not applicable. */
export type OptionLine = (productData: any, t: EmailTranslate, lang: Lang) => string | null;

const readPath = (obj: any, path: string): unknown =>
  path.split('.').reduce((acc, key) => (acc == null ? acc : acc[key]), obj);

const formatAmount = (value: unknown, lang: Lang): string | null => {
  const n = Number(value);
  return Number.isFinite(n) && n >= 0 ? new Intl.NumberFormat(lang === 'nl' ? 'nl-NL' : 'en-GB').format(n) : null;
};

export const PRODUCT_COVERAGES: Partial<Record<ProductId, CoverageDef[]>> = {
  personenauto: AUTO_OPTIONAL_COVERAGES.map((c) => ({ flag: c.key, labelKey: c.labelKey })),
  inboedel: INBOEDEL_OPTIONAL_COVERAGES.map((c) => {
    const { amountFieldPath, ecCode } = c;
    return {
      flag: c.dekkingenKey,
      labelKey: c.labelKey,
      sum: amountFieldPath
        ? (d) => readPath(d, amountFieldPath)
        : ecCode
          ? (d) => getEc(d?.Contractdocument?.PP, ecCode)?.EC_VERZSOM
          : undefined,
    };
  }),
  rechtsbijstand: RBV_COVERAGES.map((c) => ({ flag: c.key, labelKey: c.labelKey })),
};

export const PRODUCT_OPTIONS: Partial<Record<ProductId, OptionLine[]>> = {
  woonhuis: [
    // Storm eigen risico as chosen on the Woonhuisdekking card (€ 0 = afgekocht, else OP_ERBSTRM).
    (d, t, lang) => {
      const raw = d?.Contractdocument?.PP?.OP;
      const op = Array.isArray(raw) ? raw[0] : raw;
      if (!op) return null;
      const amount = formatAmount(op.OP_STORM === 'J' ? 0 : op.OP_ERBSTRM ?? 250, lang);
      return amount == null ? null : t('screens.woonhuis.coverageInfo.stormDeductible', { amount, lng: lang });
    },
  ],
  personenauto: [
    // Voluntary eigen risico, only asked on WA+volledig.
    (d, t, lang) => {
      if (d?.Supportsection?.Dekkingen?.Hoofddekking !== 'WA+volledig') return null;
      const amount = formatAmount(d?.Contractdocument?.PP?.CA?.[0]?.CA_ERB, lang);
      return amount == null ? null : `${t('screens.personenauto.fields.deductible', { lng: lang })}: € ${amount}`;
    },
  ],
};

/** Selected extra coverages of a product, labelled like the screen, with their insured sum. */
export const resolveExtras = (productData: any, id: ProductId | undefined, t: EmailTranslate, lang: Lang): string[] => {
  const defs = id ? PRODUCT_COVERAGES[id] : undefined;
  if (!defs) return [];
  const dekkingen = productData?.Supportsection?.Dekkingen || {};
  return defs
    .filter((d) => dekkingen[d.flag] === 'J')
    .map((d) => {
      const label = t(d.labelKey, { lng: lang });
      const sum = d.sum ? d.sum(productData) : undefined;
      const amount = Number(sum) > 0 ? formatAmount(sum, lang) : null;
      return amount ? `${label}: € ${amount}` : label;
    });
};

/** Chosen option lines of a product (eigen risico etc.). */
export const resolveOptions = (productData: any, id: ProductId | undefined, t: EmailTranslate, lang: Lang): string[] =>
  (id ? PRODUCT_OPTIONS[id] ?? [] : [])
    .map((line) => line(productData, t, lang))
    .filter((line): line is string => !!line);
